import { useState } from "react";
import { Copy, Check } from "lucide-react";
import ReactMarkdown from "react-markdown";

interface AIMessageProps {
  message: string;
  subtitle?: string;
  timestamp?: string;
  isStreaming?: boolean;
}

const AIMessage = ({ message, subtitle, timestamp, isStreaming = false }: AIMessageProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    console.log('Copy button clicked, message:', message);
    try {
      // Strip markdown and HTML tags from message for plain text copy
      const plainText = message
        .replace(/\*\*(.*?)\*\*/g, '$1') // Bold
        .replace(/\*(.*?)\*/g, '$1') // Italic
        .replace(/`(.*?)`/g, '$1') // Inline code
        .replace(/```[\s\S]*?```/g, '') // Code blocks
        .replace(/#{1,6}\s+(.*)/g, '$1') // Headers
        .replace(/\[(.*?)\]\(.*?\)/g, '$1') // Links
        .replace(/<[^>]*>/g, '') // HTML tags
        .replace(/\n+/g, '\n') // Multiple newlines
        .trim();
      
      console.log('Plain text to copy:', plainText);
      
      if (navigator.clipboard && navigator.clipboard.writeText) {
        console.log('Using modern clipboard API');
        await navigator.clipboard.writeText(plainText);
        console.log('Copy successful');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        console.log('Using fallback copy method');
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = plainText;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          const successful = document.execCommand('copy');
          console.log('Fallback copy result:', successful);
          if (successful) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }
        } catch (fallbackErr) {
          console.error('Fallback copy failed:', fallbackErr);
        }
        
        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  return (
    <div className="flex justify-start w-full mb-6">
      <div className="bg-gray-50/30 dark:bg-gray-800/20 rounded-lg p-4 w-full relative group" style={{ fontFamily: 'Lexend Deca' }}>
        {subtitle && (
          <div className="mb-3">
            <span className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</span>
          </div>
        )}
        <div className="prose prose-gray dark:prose-invert max-w-none text-[14px] font-normal leading-normal">
          <ReactMarkdown
            components={{
              p: ({ children }) => <p className="mb-4 last:mb-0 text-gray-800 dark:text-gray-100">{children}</p>,
              h1: ({ children }) => <h1 className="text-lg font-semibold mb-4 mt-6 first:mt-0 text-gray-900 dark:text-gray-50">{children}</h1>,
              h2: ({ children }) => <h2 className="text-base font-semibold mb-3 mt-5 first:mt-0 text-gray-900 dark:text-gray-50">{children}</h2>,
              h3: ({ children }) => <h3 className="text-sm font-semibold mb-3 mt-4 first:mt-0 text-gray-900 dark:text-gray-50">{children}</h3>,
              ul: ({ children }) => <ul className="list-none mb-4 space-y-2">{children}</ul>,
              ol: ({ children }) => <ol className="list-decimal list-inside mb-4 space-y-2 ml-2">{children}</ol>,
              li: ({ children }) => (
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 bg-gray-400 dark:bg-gray-500 rounded-full mt-2.5 mr-3 flex-shrink-0"></span>
                  <span className="text-gray-800 dark:text-gray-100">{children}</span>
                </li>
              ),
              code: ({ children, className }) => {
                const isInline = !className;
                return isInline ? (
                  <code className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-2 py-0.5 rounded-md text-sm font-mono">{children}</code>
                ) : (
                  <pre className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-4 overflow-x-auto">
                    <code className="text-sm font-mono text-gray-800 dark:text-gray-200 whitespace-pre">{children}</code>
                  </pre>
                );
              },
              pre: ({ children }) => <div className="mb-4">{children}</div>,
              strong: ({ children }) => <strong className="font-semibold text-gray-900 dark:text-gray-50">{children}</strong>,
              em: ({ children }) => <em className="italic text-gray-800 dark:text-gray-100">{children}</em>,
              blockquote: ({ children }) => (
                <blockquote className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic mb-4 text-gray-700 dark:text-gray-300">
                  {children}
                </blockquote>
              ),
              a: ({ children, href }) => (
                <a 
                  href={href} 
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline decoration-blue-600/30 hover:decoration-blue-600 transition-colors" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  {children}
                </a>
              )
            }}
          >
            {message}
          </ReactMarkdown>
        </div>
        
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
          <span className="text-[11px] text-gray-500 dark:text-gray-400 min-h-[16px] flex items-center">
            {isStreaming ? (
              <span className="flex items-center space-x-1">
                <span className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-pulse"></span>
                <span className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></span>
                <span className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></span>
              </span>
            ) : (
              <>{timestamp ? formatTimestamp(timestamp) : 'Just now'}</>
            )}
          </span>
          
          <button 
            onClick={handleCopy}
            className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-lg"
            title="Copy message"
          >
            {copied ? (
              <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
            ) : (
              <Copy className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIMessage;
